import { useState, useEffect, useMemo, useCallback } from "react";
import { getApps, updateUserPreferences, type App, type AppsViewFilter } from "../api";
import { useAuth } from "../contexts/AuthContext";

interface UseVisibleAppsResult {
  /** Apps filtered by user's visibility preference */
  apps: App[];
  /** All apps (unfiltered) - useful for showing "X of Y" counts */
  allApps: App[];
  /** Loading state */
  loading: boolean;
  /** Error message if fetch failed */
  error: string | null;
  /** Current visibility filter */
  viewFilter: AppsViewFilter;
  /** Update visibility filter (persists to user preferences) */
  setViewFilter: (filter: AppsViewFilter) => Promise<void>;
  /** Refetch apps from server */
  refetch: () => Promise<void>;
}

/**
 * Hook for accessing apps with consistent visibility filtering.
 *
 * Filters apps based on user's preference (all vs mine).
 * In the future, this can be extended to handle permission-based visibility.
 */
export function useVisibleApps(): UseVisibleAppsResult {
  const { user, refreshUser } = useAuth();

  const [allApps, setAllApps] = useState<App[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [viewFilter, setViewFilterState] = useState<AppsViewFilter>(
    user?.preferences?.appsViewFilter ?? "all"
  );

  // Filter apps based on current preference
  const apps = useMemo(() => {
    if (viewFilter === "mine" && user?.email) {
      return allApps.filter((app) => app.createdBy === user.email);
    }
    return allApps;
  }, [allApps, viewFilter, user?.email]);

  // Fetch apps from server
  const fetchApps = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getApps();
      setAllApps(data);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load apps");
    } finally {
      setLoading(false);
    }
  }, []);

  // Update filter and persist to user preferences
  const setViewFilter = useCallback(
    async (newFilter: AppsViewFilter) => {
      const previousFilter = viewFilter;
      setViewFilterState(newFilter);

      try {
        await updateUserPreferences({ appsViewFilter: newFilter });
        // Refresh user to sync preferences across components
        await refreshUser();
      } catch {
        // Rollback on error
        setViewFilterState(previousFilter);
      }
    },
    [viewFilter, refreshUser]
  );

  // Sync local state when user preferences change (e.g., from another component)
  useEffect(() => {
    const userPref = user?.preferences?.appsViewFilter;
    if (userPref && userPref !== viewFilter) {
      setViewFilterState(userPref);
    }
  }, [user?.preferences?.appsViewFilter, viewFilter]);

  // Initial fetch
  useEffect(() => {
    fetchApps();
  }, [fetchApps]);

  return {
    apps,
    allApps,
    loading,
    error,
    viewFilter,
    setViewFilter,
    refetch: fetchApps,
  };
}
